import React from 'react';
import { Poppins } from 'src/common';
import { VariablePill } from 'src/common/VariablePill';
import { IconVariableWorkspace } from 'src/assets/dynamicSvg';
import { formatNumber } from 'src/utils/misc';
import { CostDisplay } from './util';
import * as S from './CostCellContent.styles';

/** Small tinted circle marking a value as formula-derived ("calculated"). */
export const CalculatedIcon: React.FC = () => (
  <S.CalcIconCircle data-cy="cost-calculated-icon">
    <IconVariableWorkspace />
  </S.CalcIconCircle>
);

export const MissingVariablesPill: React.FC<{ onClick?: () => void; missingNames?: string[] }> = ({
  onClick,
  missingNames,
}) => (
  <VariablePill
    unpopulated
    tag="div"
    onClick={onClick}
    style={onClick ? { cursor: 'pointer' } : undefined}
    title={missingNames?.length ? `Missing: ${missingNames.join(', ')}` : undefined}
  >
    <Poppins px={12} weight={500} color="statusRed" nowrap>
      Missing variables
    </Poppins>
  </VariablePill>
);

interface CostCellContentProps {
  display: CostDisplay;
  onMissingClick?: () => void;
  /** Hover reveals which variables are missing (epic AC3). */
  missingNames?: string[];
}

/**
 * Read-only rendering of a resolved cost display state — used by table cells and
 * detail sheets. Inline *editing* stays with the callers (RMP cost cells).
 */
export const CostCellContent: React.FC<CostCellContentProps> = ({ display, onMissingClick, missingNames }) => {
  switch (display.state) {
    case 'value':
      return (
        <S.Content>
          <Poppins px={14} data-cy="cost-value">
            {formatNumber(display.value, { symbol: true, padDecimals: true })}
          </Poppins>
          {display.calculated && <CalculatedIcon />}
        </S.Content>
      );
    case 'missing':
      return <MissingVariablesPill onClick={onMissingClick} missingNames={missingNames} />;
    case 'formula':
      return (
        <S.Content>
          <Poppins px={14} data-cy="cost-formula-label">
            Formula
          </Poppins>
          <CalculatedIcon />
        </S.Content>
      );
    case 'blank':
      return <Poppins px={14}>-</Poppins>;
  }
};
